import type { TestLink } from '../../services/sifa/types';
import { formatZeit } from './unterweisungModulLogic';

/**
 * Regeln des Test-Link-Dialogs (docs/sifa-ausbau2-plan.md §0.1) — ohne DOM.
 * Der Dialog selbst (TestLinkDialog.tsx) ist nicht testbar; die Texte, die
 * Entscheidung „vor dem Test speichern?" und das Wiederverwenden der Links
 * sind es.
 */

export const TEST_HINWEIS = 'Testlauf: nichts wird gespeichert, kein Zertifikat.';

export const TEST_TOOLTIP = 'Legt einen 24-h-Test-Link an; nichts am Kurs wird geändert.';

export const FRAGE_ERST_SPEICHERN =
  'Erst speichern und dann testen? Getestet wird der gespeicherte Stand — ohne Speichern fehlen die letzten Änderungen im Test.';

export const FRAGE_TESTEN_BEI_KONFLIKT =
  'Speichern geht gerade nicht: die Vorlage wurde inzwischen von jemand anderem geändert. Den gespeicherten Stand testen? Die eigenen Änderungen bleiben im Editor, bis „Neu laden" sie verwirft.';

export const KOPIEREN_FEHLER = 'Kopieren war nicht möglich — bitte den Link unten markieren und kopieren.';

/** „gültig bis 08.10.2026, 14:30"; ohne lesbares Datum nichts. */
export const gueltigBisText = (iso: string | null | undefined): string => {
  const s = (iso || '').trim();
  if (!s || Number.isNaN(Date.parse(s))) return '';
  return `gültig bis ${formatZeit(s)}`;
};

/**
 * Was „Testen" tut:
 *  - `direkt`: nichts Ungespeichertes (oder Nur-Ansicht, die nichts aendern
 *    kann) — der gespeicherte Stand wird getestet;
 *  - `fragen`: Ungespeichertes — speichern und testen, nur den gespeicherten
 *    Stand testen oder abbrechen;
 *  - `konflikt`: Speichern scheitert gerade an der optimistischen Sperre
 *    (Vorlage) — angeboten wird nur der gespeicherte Stand.
 */
export type TestAblauf = 'direkt' | 'fragen' | 'konflikt';

export const testAblauf = (e: { dirty: boolean; gesperrt: boolean; konflikt: boolean }): TestAblauf => {
  if (!e.dirty || e.gesperrt) return 'direkt';
  return e.konflikt ? 'konflikt' : 'fragen';
};

// ─── Wiederverwenden ────────────────────────────────────────────────────────

/** Ein gemerkter Link wird nur genommen, solange er noch mindestens so lange gilt. */
export const MINDEST_RESTZEIT_MS = 60 * 60 * 1000;

export const linkNochBrauchbar = (l: Pick<TestLink, 'gueltigBis'>, jetzt: number = Date.now()): boolean => {
  const bis = Date.parse((l?.gueltigBis || '').trim());
  return Number.isFinite(bis) && bis - jetzt >= MINDEST_RESTZEIT_MS;
};

/**
 * Gemerkte Links je `<userId>:<art>:<id>`, solange die Seite offen ist. Jeder
 * Klick auf „Testen" legte sonst eine neue Zeile in sifa_testlinks an — der
 * Server deckelt die Zahl, und zehn gueltige Links fuer denselben Kurs helfen
 * niemandem. Bewusst nur im Speicher: nach dem Neuladen gibt es einen neuen.
 *
 * Der NUTZER gehoert in den Schluessel: meldet sich am selben Rechner ohne
 * Neuladen ein anderer an, darf er nicht den Link des Vorgaengers bekommen —
 * die RPC haette ihn womoeglich abgelehnt. Ohne bekannten Nutzer: kein Cache.
 */
const gemerkt = new Map<string, TestLink>();
/**
 * Laufende Anfragen, gleicher Schluessel. React StrictMode ruft den Effekt
 * des Dialogs zweimal auf; beide Aufrufe teilen dieselbe Anfrage, statt zwei
 * Links anzulegen. Ebenso ein schneller zweiter Klick.
 */
const laufend = new Map<string, Promise<TestLink>>();
/**
 * Zaehlt jedes Vergessen hoch. Eine Anfrage, die vor dem Vergessen startete
 * und danach antwortet (Abmelden waehrend des Ladens), schreibt nicht mehr
 * in den Cache.
 */
let generation = 0;
/** Der Nutzer der letzten Anfrage — wechselt er, wird alles vergessen. */
let letzterNutzer: string | null = null;

export type TestLinkAnleger = (art: 'unterweisung' | 'vorlage', id: string) => Promise<TestLink>;

/** Alles Gemerkte vergessen; laufende Anfragen duerfen danach nichts mehr eintragen. */
export const vergesseTestLinks = (): void => {
  generation += 1;
  gemerkt.clear();
  laufend.clear();
  letzterNutzer = null;
};

/**
 * Reaktion auf supabase.auth.onAuthStateChange: Abmelden und geaenderte
 * Nutzerdaten vergessen alles, ebenso jeder Wechsel des angemeldeten Nutzers.
 * Ein TOKEN_REFRESHED desselben Nutzers laesst den Cache stehen.
 */
export const beiAuthEreignis = (ereignis: string, userId: string | null | undefined): void => {
  const uid = (userId ?? '').trim() || null;
  if (ereignis === 'SIGNED_OUT' || ereignis === 'USER_UPDATED' || uid !== letzterNutzer) vergesseTestLinks();
};

/**
 * Der Test-Link fuer `art`+`id`: der gemerkte, solange er noch eine Stunde
 * gilt, sonst ein neuer ueber `anlegen` (legeTestLinkAn). `neu` erzwingt
 * einen neuen („Neuen Link erzeugen"). Fehler werden nicht gemerkt. Ohne
 * `userId` wird weder gelesen noch gemerkt noch geteilt.
 */
export const holeTestLink = (
  art: 'unterweisung' | 'vorlage',
  id: string,
  anlegen: TestLinkAnleger,
  opts: { userId?: string | null; neu?: boolean; jetzt?: number } = {},
): Promise<TestLink> => {
  const uid = (opts.userId ?? '').trim();
  if (!uid) return anlegen(art, id);
  if (uid !== letzterNutzer) {
    vergesseTestLinks();
    letzterNutzer = uid;
  }

  const schluessel = `${uid}:${art}:${(id || '').trim()}`;
  if (!opts.neu) {
    const l = gemerkt.get(schluessel);
    if (l && linkNochBrauchbar(l, opts.jetzt ?? Date.now())) return Promise.resolve(l);
  }
  const offen = laufend.get(schluessel);
  if (offen) return offen;

  const start = generation;
  const anfrage = anlegen(art, id)
    .then((l) => {
      if (start === generation) gemerkt.set(schluessel, l);
      return l;
    })
    .finally(() => {
      if (laufend.get(schluessel) === anfrage) laufend.delete(schluessel);
    });
  laufend.set(schluessel, anfrage);
  return anfrage;
};
